/**
 * compose.js — turn recognised boxes into a translated picture.
 *
 * This is the half every competitor is judged on and most get wrong. Four
 * things decide whether the result reads as a translation or as a defaced
 * photo, and all four were found by LOOKING at output, not by running tests:
 *
 *  1. WHAT gets translated. PP-OCR emits per-phrase boxes, so a naive pipeline
 *     sends "Grilled" and "salmon" to the translator separately and gets two
 *     unrelated words back. Blocks are merged along a line first, and split
 *     again only where the horizontal gap is wide enough to be a column.
 *  2. WHERE the original actually is. Detection runs on a copy scaled to 960 px,
 *     so its boxes are loose when mapped back. Erasing that box leaves glyph
 *     slivers at the ends of a line AND covers far more paper than needed. The
 *     ink is re-measured in original pixels before anything is painted.
 *  3. HOW the original is erased. A flat fill of one sampled colour is what the
 *     free tools do, and it is why their output shows a rectangle — real paper
 *     carries a gradient. Pixels are interpolated inward from the untouched
 *     surroundings instead.
 *  4. HOW the replacement is set. Translations change length — German runs ~30%
 *     longer than English — so the type must shrink and wrap into the space
 *     that exists, and must never spill outside the object it belongs to.
 *
 * Exposed as window.PTCompose.
 */
(function (root) {
    'use strict';
    if (root.PTCompose) return;

    // ── block grouping ───────────────────────────────────────────────────

    /**
     * Merge recognised boxes into translatable blocks.
     *
     * Same row → same block, unless the gap between two boxes exceeds
     * `gapFactor` times the line height. That threshold is what keeps a menu's
     * dish and its price apart (one row, two columns) while still joining the
     * words of a sentence.
     */
    /**
     * Is this text written right to left? Arabic and Hebrew letters make up at
     * least half of its letters. Measured on an Arabic exit sign the detector
     * split into two boxes: joined left to right, «مخرج الطوارئ» became
     * «الطوارئ مخرج» — every word right, the phrase backwards.
     */
    var RTL_LETTER = /[\u0590-\u05FF\u0600-\u06FF\u0750-\u077F\uFB1D-\uFDFF\uFE70-\uFEFF]/g;
    var ANY_LETTER = /\p{L}/gu;
    function isRtl(text) {
        var s = String(text == null ? '' : text);
        var rtl = (s.match(RTL_LETTER) || []).length;
        return rtl > 0 && rtl >= 0.5 * (s.match(ANY_LETTER) || []).length;
    }

    function groupBlocks(lines, gapFactor) {
        gapFactor = gapFactor || 1.6;
        var byRow = {};
        lines.forEach(function (l) {
            (byRow[l.row] = byRow[l.row] || []).push(l);
        });
        var blocks = [];
        Object.keys(byRow).forEach(function (k) {
            var items = byRow[k].slice().sort(function (a, b) { return a.box.x - b.box.x; });
            var cur = null;
            items.forEach(function (it) {
                if (cur) {
                    var gap = it.box.x - (cur.box.x + cur.box.w);
                    var hh = Math.max(cur.box.h, it.box.h);
                    if (gap <= gapFactor * hh) {
                        var x0 = Math.min(cur.box.x, it.box.x);
                        var y0 = Math.min(cur.box.y, it.box.y);
                        var x1 = Math.max(cur.box.x + cur.box.w, it.box.x + it.box.w);
                        var y1 = Math.max(cur.box.y + cur.box.h, it.box.y + it.box.h);
                        cur.box = { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
                        cur.text += ' ' + it.text;
                        cur.parts.push(it);
                        return;
                    }
                }
                cur = {
                    box: { x: it.box.x, y: it.box.y, w: it.box.w, h: it.box.h },
                    text: it.text, parts: [it], row: Number(k),
                };
                blocks.push(cur);
            });
        });
        // Geometry merged left to right above; the WORDS of a right-to-left block
        // are read right to left.
        blocks.forEach(function (b) {
            if (b.parts.length < 2 || !isRtl(b.text)) return;
            b.text = b.parts.slice().sort(function (p, q) { return q.box.x - p.box.x; })
                .map(function (p) { return p.text; }).join(' ');
        });
        return blocks.sort(function (a, b) { return a.box.y - b.box.y || a.box.x - b.box.x; });
    }

    // ── colour + geometry ────────────────────────────────────────────────

    function clamp255(v) { return v < 0 ? 0 : v > 255 ? 255 : v | 0; }
    function rgbStr(c) { return 'rgb(' + clamp255(c[0]) + ',' + clamp255(c[1]) + ',' + clamp255(c[2]) + ')'; }
    function luma(c) { return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2]; }

    /**
     * Read the paper colour from a ring just OUTSIDE the box, and the ink colour
     * from the pixels inside it furthest from the paper.
     *
     * The paper is a MEDIAN, not a mean: on a rotated sign the ring clips the
     * grey world beyond the sign's edge, and a mean drags the fill towards that
     * grey — which is exactly how the first build produced visibly wrong slabs.
     */
    function sampleColors(ctx, box, pad) {
        pad = pad || Math.max(2, Math.round(box.h * 0.3));
        var cw = ctx.canvas.width, ch = ctx.canvas.height;
        var x0 = Math.max(0, box.x - pad), y0 = Math.max(0, box.y - pad);
        var x1 = Math.min(cw, box.x + box.w + pad), y1 = Math.min(ch, box.y + box.h + pad);
        if (x1 <= x0 || y1 <= y0) return null;

        var img = ctx.getImageData(x0, y0, x1 - x0, y1 - y0);
        var d = img.data, W = x1 - x0, H = y1 - y0;
        var inX0 = box.x - x0, inY0 = box.y - y0, inX1 = inX0 + box.w, inY1 = inY0 + box.h;

        var ring = [], inside = [];
        for (var y = 0; y < H; y++) {
            for (var x = 0; x < W; x++) {
                var i = (y * W + x) * 4;
                var px = [d[i], d[i + 1], d[i + 2]];
                var isInside = x >= inX0 && x < inX1 && y >= inY0 && y < inY1;
                (isInside ? inside : ring).push(px);
            }
        }
        if (!ring.length || !inside.length) return null;

        var paper = [0, 1, 2].map(function (c) {
            var v = ring.map(function (p) { return p[c]; }).sort(function (a, b) { return a - b; });
            return v[v.length >> 1];
        });
        var paperL = luma(paper);

        var scored = inside.map(function (p) { return { p: p, d: Math.abs(luma(p) - paperL) }; })
            .sort(function (a, b) { return b.d - a.d; });
        var take = Math.max(1, Math.round(scored.length * 0.18));
        var ink = [0, 0, 0];
        for (var k = 0; k < take; k++) {
            ink[0] += scored[k].p[0]; ink[1] += scored[k].p[1]; ink[2] += scored[k].p[2];
        }
        ink = ink.map(function (v) { return v / take; });

        var varSum = 0;
        for (var r = 0; r < ring.length; r++) varSum += Math.abs(luma(ring[r]) - paperL);
        var flatness = 1 - Math.min(1, (varSum / ring.length) / 40);

        return {
            paper: paper, ink: ink, flatness: flatness,
            contrast: Math.abs(luma(ink) - paperL) / 255,
        };
    }

    /**
     * Tighten a detection box down to the pixels that actually carry ink.
     *
     * Detection works on a 960 px copy, so a box mapped back to full resolution
     * is loose and imprecise. Two visible failures follow from painting it as
     * given: green slivers of the original survive past the ends of the erase,
     * and the erase itself covers a slab of clean paper. Measuring the ink in
     * ORIGINAL pixels fixes both and gives the replacement type the same
     * baseline and left edge the original had.
     */
    function refineToInk(ctx, box, paper) {
        var cw = ctx.canvas.width, ch = ctx.canvas.height;
        var x0 = Math.max(0, box.x | 0), y0 = Math.max(0, box.y | 0);
        var x1 = Math.min(cw, x0 + (box.w | 0)), y1 = Math.min(ch, y0 + (box.h | 0));
        if (x1 <= x0 || y1 <= y0) return null;

        var W = x1 - x0, H = y1 - y0;
        var d = ctx.getImageData(x0, y0, W, H).data;
        var pl = luma(paper);
        // Well above sensor noise and JPEG ringing, well below the glyph body.
        var cut = 26;

        var col = new Uint32Array(W), rowp = new Uint32Array(H);
        for (var y = 0; y < H; y++) {
            for (var x = 0; x < W; x++) {
                var i = (y * W + x) * 4;
                if (Math.abs(luma([d[i], d[i + 1], d[i + 2]]) - pl) < cut) continue;
                col[x]++; rowp[y]++;
            }
        }

        /**
         * Take the DENSEST contiguous run, not the extreme bounds.
         *
         * A plain min/max bounding box latches onto anything high-contrast that
         * happens to fall inside the loose detection box — on the pharmacy sign
         * it grabbed the board's own dark edge, stretched the region past the
         * board, and the erase then interpolated the grey background INTO the
         * sign. Text is a dense band with word-sized gaps; a border is a narrow
         * strip separated by clean paper, so bridging only small gaps keeps one
         * and drops the other.
         */
        function densestRun(profile, gapAllowed) {
            var best = null, cur = null, gap = 0;
            for (var k = 0; k < profile.length; k++) {
                if (profile[k] > 0) {
                    if (!cur) cur = { a: k, b: k, mass: 0 };
                    cur.b = k; cur.mass += profile[k]; gap = 0;
                } else if (cur) {
                    gap++;
                    if (gap > gapAllowed) {
                        if (!best || cur.mass > best.mass) best = cur;
                        cur = null; gap = 0;
                    }
                }
            }
            if (cur && (!best || cur.mass > best.mass)) best = cur;
            return best;
        }

        // Word spaces run about one line-height wide; a border sits further off.
        var cRun = densestRun(col, Math.max(4, Math.round(H * 1.1)));
        var rRun = densestRun(rowp, Math.max(2, Math.round(H * 0.34)));
        if (!cRun || !rRun) return null;

        return {
            x: x0 + cRun.a, y: y0 + rRun.a,
            w: cRun.b - cRun.a + 1, h: rRun.b - rRun.a + 1,
        };
    }

    // ── erase ────────────────────────────────────────────────────────────

    /**
     * Erase the original lettering by extending the surrounding pixels inward.
     *
     * A flat fill of one sampled colour leaves a rectangle, because real signage
     * and paper carry a gradient — a vignette, a light fall-off, a shadow — and
     * one colour matches it in exactly one place. Measured on the pharmacy sign,
     * the flat fill left three clearly visible lighter slabs.
     *
     * Every pixel here is interpolated between the real pixels immediately
     * outside the region on both axes. That follows any smooth gradient for
     * free, needs no model, and collapses to a flat fill when the surroundings
     * really are flat — the behaviour wanted in both cases.
     */
    function eraseBox(ctx, box, colors, opts) {
        opts = opts || {};
        var cw = ctx.canvas.width, ch = ctx.canvas.height;
        var pad = Math.max(2, Math.round(box.h * 0.14));
        var x = Math.max(0, box.x - pad);
        var y = Math.max(0, box.y - pad);
        var w = Math.min(cw, box.x + box.w + pad) - x;
        var h = Math.min(ch, box.y + box.h + pad) - y;
        if (w <= 0 || h <= 0) return;

        var sx = Math.max(0, x - 1), sy = Math.max(0, y - 1);
        var sw = Math.min(cw, x + w + 1) - sx;
        var sh = Math.min(ch, y + h + 1) - sy;
        var s = ctx.getImageData(sx, sy, sw, sh).data;
        function at(px, py) {
            var a = Math.min(sw - 1, Math.max(0, px));
            var b = Math.min(sh - 1, Math.max(0, py));
            return (b * sw + a) * 4;
        }

        var out = ctx.createImageData(w, h);
        var o = out.data;
        var ox = x - sx, oy = y - sy;
        var leftX = ox - 1, rightX = ox + w, topY = oy - 1, botY = oy + h;
        // On a wide, thin line the vertical neighbours carry the real gradient;
        // the horizontal ones are hundreds of pixels away and say little.
        var wv = w >= h ? 0.68 : 0.32;

        /**
         * A neighbour that is nothing like the paper is not paper: it is the
         * world past the edge of the sign, the page or the label. Interpolating
         * from it drags that colour inside the object and smears it — visible
         * on the pharmacy sign as a grey wash across the lower right. When an
         * edge sample looks alien, the sampled paper stands in for it.
         */
        var paper = (colors && colors.paper) || [255, 255, 255];
        var paperL = luma(paper);
        function edge(idx, c) {
            var v = s[idx + c];
            var l = luma([s[idx], s[idx + 1], s[idx + 2]]);
            return Math.abs(l - paperL) > 46 ? paper[c] : v;
        }

        /**
         * Confine the repaint to the object the text sits on, per scanline.
         *
         * The erase region is a rectangle and the sign under it is rotated, so
         * the rectangle crosses the board's slanted edge; painted wholesale it
         * spills cream over the grey world outside — plainly visible at the
         * bottom-right of the pharmacy sign.
         *
         * A per-pixel "is this paper or ink?" test was tried and is WRONG twice
         * over. Anti-aliased glyph edges match neither and survive as ghost
         * outlines of the original words — that regression is in the history of
         * this file for a reason. And it cannot work in principle here: on a
         * cream sign with dark green type, mid-grey background sits almost
         * exactly on the paper↔ink line in RGB, about 13 units off it, so no
         * colour threshold separates them.
         *
         * The reliable signal is spatial, not chromatic. Text is always
         * SURROUNDED by paper on its own row; the outside world lies beyond the
         * last paper pixel of that row. So each scanline is repainted only
         * between its first and last paper-like pixel — glyph edges, which sit
         * between paper pixels, are covered; the world past the board's edge is
         * not touched.
         */
        function isPaper(idx) {
            var a = s[idx] - paper[0], b = s[idx + 1] - paper[1], c2 = s[idx + 2] - paper[2];
            return a * a + b * b + c2 * c2 <= 62 * 62;
        }

        for (var j = 0; j < h; j++) {
            var li = at(leftX, oy + j), ri = at(rightX, oy + j);
            var fy = h > 1 ? j / (h - 1) : 0;

            var first = -1, last = -1;
            for (var q = 0; q < w; q++) {
                if (!isPaper(at(ox + q, oy + j))) continue;
                if (first < 0) first = q;
                last = q;
            }

            for (var i = 0; i < w; i++) {
                var k = (j * w + i) * 4;
                var self = at(ox + i, oy + j);
                // No paper on this row at all, or outside its paper span → the
                // original pixel stands.
                if (first < 0 || i < first || i > last) {
                    o[k] = s[self]; o[k + 1] = s[self + 1]; o[k + 2] = s[self + 2]; o[k + 3] = 255;
                    continue;
                }
                var ti = at(ox + i, topY), bi = at(ox + i, botY);
                var fx = w > 1 ? i / (w - 1) : 0;
                for (var c = 0; c < 3; c++) {
                    var horiz = edge(li, c) * (1 - fx) + edge(ri, c) * fx;
                    var vert = edge(ti, c) * (1 - fy) + edge(bi, c) * fy;
                    o[k + c] = clamp255(vert * wv + horiz * (1 - wv));
                }
                o[k + 3] = 255;
            }
        }
        ctx.putImageData(out, x, y);
    }

    // ── render ───────────────────────────────────────────────────────────

    var FONT_STACK = '"Noto Sans", "Segoe UI", Arial, "Helvetica Neue", sans-serif';

    function wrap(ctx, text, maxW) {
        var words = String(text).split(/\s+/).filter(Boolean);
        if (!words.length) return [''];
        var out = [], cur = words[0];
        for (var i = 1; i < words.length; i++) {
            var t = cur + ' ' + words[i];
            if (ctx.measureText(t).width <= maxW) cur = t;
            else { out.push(cur); cur = words[i]; }
        }
        out.push(cur);
        return out;
    }

    /**
     * Fit `text` inside `box`: the largest size that stays within BOTH the width
     * and the height, wrapping onto as many lines as the height allows.
     *
     * The height budget is deliberately allowed to grow a little (a translation
     * that needs two lines where the original had one is normal), but the WIDTH
     * is hard — overflowing it is what pushed "Lenin street, 42" off the edge of
     * the sign in the first build, and a line that leaves its object is worse
     * than a line that is small.
     */
    function fitText(ctx, text, box, opts) {
        opts = opts || {};
        var weight = opts.weight || '600';
        var maxLines = opts.maxLines || 3;
        var maxSize = Math.round(Math.min(box.h * 0.98, box.h));
        var minSize = Math.max(8, Math.round(box.h * 0.26));

        for (var size = maxSize; size >= minSize; size -= 1) {
            ctx.font = weight + ' ' + size + 'px ' + FONT_STACK;
            var lines = wrap(ctx, text, box.w);
            if (lines.length > maxLines) continue;
            var widest = 0;
            for (var i = 0; i < lines.length; i++) {
                widest = Math.max(widest, ctx.measureText(lines[i]).width);
            }
            if (widest > box.w) continue;
            var lineH = size * 1.14;
            if (lines.length * lineH <= box.h * 1.5) {
                return { size: size, lines: lines, lineH: lineH };
            }
        }

        // Nothing fits cleanly. Squeeze horizontally rather than spill: a
        // condensed line inside the sign beats a correct-width line outside it.
        ctx.font = weight + ' ' + minSize + 'px ' + FONT_STACK;
        var last = wrap(ctx, text, box.w);
        var wMax = 0;
        for (var j = 0; j < last.length; j++) wMax = Math.max(wMax, ctx.measureText(last[j]).width);
        return {
            size: minSize, lines: last, lineH: minSize * 1.14,
            squeeze: wMax > box.w ? box.w / wMax : 1,
        };
    }

    /** Where the block sat inside the image decides how the replacement aligns. */
    function alignFor(box, imgW) {
        var centre = box.x + box.w / 2;
        var rel = centre / imgW;
        if (box.x < imgW * 0.06) return 'left';
        if (box.x + box.w > imgW * 0.94) return 'right';
        if (Math.abs(rel - 0.5) < 0.08) return 'center';
        return 'left';
    }

    function drawText(ctx, box, text, colors, opts) {
        opts = opts || {};
        var fit = fitText(ctx, text, box, opts);
        var align = opts.align || 'left';

        ctx.save();
        ctx.font = (opts.weight || '600') + ' ' + fit.size + 'px ' + FONT_STACK;
        ctx.fillStyle = opts.color || rgbStr(colors.ink);
        ctx.textBaseline = 'top';
        ctx.direction = opts.rtl ? 'rtl' : 'ltr';
        ctx.textAlign = align === 'center' ? 'center' : (align === 'right' ? 'right' : 'left');

        var totalH = fit.lines.length * fit.lineH;
        var y = box.y + Math.max(0, (box.h - totalH) / 2);
        var x = align === 'center' ? box.x + box.w / 2
            : align === 'right' ? box.x + box.w
                : box.x;

        if (fit.squeeze && fit.squeeze < 1) {
            // Condense in place: scale X about the alignment anchor so the text
            // stays inside the box on every alignment.
            ctx.translate(x, 0);
            ctx.scale(fit.squeeze, 1);
            ctx.translate(-x, 0);
        }

        if (opts.halo) {
            ctx.strokeStyle = rgbStr(colors.paper);
            ctx.lineWidth = Math.max(1, fit.size * 0.09);
            ctx.lineJoin = 'round';
            for (var i = 0; i < fit.lines.length; i++) {
                ctx.strokeText(fit.lines[i], x, y + i * fit.lineH);
            }
        }
        for (var j = 0; j < fit.lines.length; j++) {
            ctx.fillText(fit.lines[j], x, y + j * fit.lineH);
        }
        ctx.restore();
        return fit;
    }

    root.PTCompose = {
        isRtl: isRtl,
        groupBlocks: groupBlocks,
        sampleColors: sampleColors,
        refineToInk: refineToInk,
        eraseBox: eraseBox,
        drawText: drawText,
        fitText: fitText,
        alignFor: alignFor,
        rgbStr: rgbStr,
        luma: luma,
    };
})(window);
